import { Image } from '@vivaldis/antd-ui'
import { endOfDay, isBefore, isValid, parseISO, startOfDay } from 'date-fns'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { EmployeeProfileQuery_employee } from '../../../../typings/EmployeeProfileQuery'
import { Information } from '../../../../../Information'
import { MissingInformationTag } from '../../../MissingInformationTag'
import { NotSetImageValue } from '../../../NotSetImageValue'
import { ExpiredInformationTag } from '../../../ExpiredInformationTag'

interface Props {
  employee: EmployeeProfileQuery_employee
}

export const ResidencePermit = ({ employee }: Props) => {
  const [t] = useTranslation('web_ui')

  const { profile, workProfile } = employee

  // is true when null or undefined to show a short form (only id card fields)
  const eeaMember = profile?.nationalityCountry?.eeaMember ?? true

  // residencePermit
  const residencePermitBackPicture = workProfile?.residencePermitBackPicture
  const residencePermitFrontPicture = workProfile?.residencePermitFrontPicture
  const residencePermitExpiresOn = workProfile?.residencePermitExpiresOn
  const residencePermitCode = workProfile?.residencePermitCode

  const isExpiredResidencePermitExpiresOnInformation = useMemo(() => {
    if (!residencePermitExpiresOn) {
      return false
    }

    const expiresAtDate = parseISO(residencePermitExpiresOn)
    if (!isValid(expiresAtDate)) {
      return true
    }

    return isBefore(endOfDay(expiresAtDate), startOfDay(new Date()))
  }, [residencePermitExpiresOn])

  if (eeaMember) {
    return null
  }

  return (
    <>
      <Information.InformationSectionHeader>
        {t('employee_profile.residence_permit_header')}
      </Information.InformationSectionHeader>
      {residencePermitFrontPicture || residencePermitBackPicture ? (
        <Information.InformationFieldWithTwoImages
          leftLabel={t('employee_profile.residence_permit_front_picture')}
          rightLabel={t('employee_profile.residence_permit_back_picture')}
          leftContent={
            <>
              {residencePermitFrontPicture ? (
                <Image
                  width={238}
                  height={160}
                  src={String(residencePermitFrontPicture)}
                />
              ) : (
                <div>
                  <div>
                    <MissingInformationTag />
                  </div>
                  <NotSetImageValue />
                </div>
              )}
            </>
          }
          rightContent={
            <>
              {residencePermitBackPicture ? (
                <Image
                  width={238}
                  height={160}
                  src={String(residencePermitBackPicture)}
                />
              ) : (
                <div>
                  <div>
                    <MissingInformationTag />
                  </div>
                  <NotSetImageValue />
                </div>
              )}
            </>
          }
        />
      ) : null}
      <Information.InformationField
        label={t('employee_profile.residence_permit_code')}
      >
        {residencePermitCode ?? <MissingInformationTag />}
      </Information.InformationField>
      <Information.InformationField
        label={t('employee_profile.residence_permit_expires_on')}
      >
        {residencePermitExpiresOn ? (
          isExpiredResidencePermitExpiresOnInformation ? (
            <ExpiredInformationTag />
          ) : (
            residencePermitExpiresOn
          )
        ) : (
          <MissingInformationTag />
        )}
      </Information.InformationField>
    </>
  )
}
